// src/pages/Designers/DesignerCreate.jsx
import React, { useState, useEffect } from "react";
import { API_URL } from '../../Redux/apiSlice';

/* endpoints */
const FILE_UPLOAD_URL = `${API_URL}/file/`;
const DESIGNER_CREATE_URL = `${API_URL}/designer`;
const CATEGORY_LIST_URL = `${API_URL}/designer/categories/list`;

export default function DesignerCreate() {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    categoryId: "",
    gender: "",
    address: "",
    picture: null,
    picturePreview: null,
    bannerImage: null,
    bannerImagePreview: null,
    portfolio: "",
    metaTag: "",
  });

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const getAuthHeader = () => {
    try {
      const raw = localStorage.getItem("knighthuntToken") || "";
      const token = raw.trim();
      if (!token) return null;
      return token.toLowerCase().startsWith("bearer ")
        ? token
        : `Bearer ${token}`;
    } catch {
      return null;
    }
  };

  /* Fetch Categories */
  useEffect(() => {
    (async () => {
      try {
        const auth = getAuthHeader();
        const res = await fetch(CATEGORY_LIST_URL, {
          headers: auth ? { Authorization: auth } : {},
        });
        const data = await res.json();

        if (res.ok && Array.isArray(data?.response)) {
          setCategories(data.response);
        }
      } catch {
        setCategories([]);
      }
      setLoadingCategories(false);
    })();
  }, []);

  const onChangeText = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  /* Image Picker (Reusable) */
  const pickImage = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setStatusMsg({ type: "error", text: "Only image files allowed." });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setStatusMsg({ type: "error", text: "Image must be under 10MB." });
      return;
    }

    const previewKey = `${type}Preview`;
    if (form[previewKey]) URL.revokeObjectURL(form[previewKey]);

    const previewUrl = URL.createObjectURL(file);

    setForm((s) => ({
      ...s,
      [type]: file,
      [previewKey]: previewUrl,
    }));

    setStatusMsg(null);
  };

  /* Upload Image */
  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("image", file);

    const auth = getAuthHeader();
    const resp = await fetch(FILE_UPLOAD_URL, {
      method: "POST",
      body: fd,
      headers: auth ? { Authorization: auth } : {},
      credentials: "include",
    });

    const body = await resp.json().catch(() => null);
    return {
      ok: resp.ok,
      url: body?.response?.url || body?.data?.url || body?.url || "",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatusMsg(null);

    try {
      let pictureUrl = "";
      let bannerUrl = "";

      if (form.picture) {
        const picRes = await uploadImage(form.picture);
        if (!picRes.ok) throw new Error("Picture upload failed");
        pictureUrl = picRes.url;
      }

      if (form.bannerImage) {
        const bannerRes = await uploadImage(form.bannerImage);
        if (!bannerRes.ok) throw new Error("Banner upload failed");
        bannerUrl = bannerRes.url;
      }
      // console.log("url", bannerUrl);
      const payload = {
        name: form.name,
        slug: form.slug,
        categoryId: form.categoryId,
        gender: form.gender,
        address: form.address,
        portfolio: form.portfolio,
        metaTag: form.metaTag,
        pic: pictureUrl,
        bannerImage: bannerUrl,
      };

      const auth = getAuthHeader();
      const resp = await fetch(DESIGNER_CREATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(auth ? { Authorization: auth } : {}),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!resp.ok) throw new Error("Designer creation failed");

      setStatusMsg({ type: "success", text: "Designer created successfully!" });
      resetForm();
    } catch (err) {
      setStatusMsg({ type: "error", text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    if (form.picturePreview) URL.revokeObjectURL(form.picturePreview);
    if (form.bannerImagePreview) URL.revokeObjectURL(form.bannerImagePreview);

    setForm({
      name: "",
      slug: "",
      categoryId: "",
      gender: "",
      address: "",
      picture: null,
      picturePreview: null,
      bannerImage: null,
      bannerImagePreview: null,
      portfolio: "",
      metaTag: "",
    });
  };

  return (
    <div style={{ padding: 20, paddingTop: 100, maxWidth: 700, margin: "0 auto" }}>
      {statusMsg && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 6,
            fontWeight: 500,
            background: statusMsg.type === "error" ? "#ffe5e5" : "#e6fff2",
            color: statusMsg.type === "error" ? "#b30000" : "#00663d",
          }}
        >
          {statusMsg.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-4 border rounded bg-light">

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Name </label>
          <input
            name="name"
            value={form.name}
            onChange={onChangeText}
            required
            className="form-control"
            style={{ marginTop: 6 }}
          />
        </div>

        {/* Slug */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Slug</label>
          <input className="form-control " name="slug" style={{ marginTop: 6 }} value={form.slug} onChange={onChangeText} required />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Category </label>
          <select
            name="categoryId"
            value={form.categoryId}
            onChange={onChangeText}
            required
            className="form-control"
            style={{ marginTop: 6 }}
          >
            <option value="">
              {loadingCategories ? "Loading..." : "Select Category"}
            </option>

            {!loadingCategories &&
              categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Gender</label>
          <select
            name="gender"
            value={form.gender}
            onChange={onChangeText}
            className="form-control"
            style={{ marginTop: 6 }}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="unisex">Unisex</option>
          </select>
        </div>

        {/* Address */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Address</label>
          <textarea
            name="address"
            value={form.address}
            onChange={onChangeText}
            rows={3}
            className="form-control"
            style={{ marginTop: 6 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Portfolio</label>
          <input
            name="portfolio"
            value={form.portfolio}
            onChange={onChangeText}
            className="form-control"
            style={{ marginTop: 6 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Meta Tag</label>
          <input
            name="metaTag"
            value={form.metaTag}
            onChange={onChangeText}
            className="form-control"
            style={{ marginTop: 6 }}
          />
        </div>
        {/* Picture */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Picture</label>
          <input type="file" className="form-control " style={{ marginTop: 6 }} onChange={(e) => pickImage(e, "picture")} />
          {form.picturePreview && <img src={form.picturePreview} alt="pic" width={100} className="mb-3" />}
        </div>
        {/* Banner */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Banner</label>
          <input type="file" className="form-control " style={{ marginTop: 6 }} onChange={(e) => pickImage(e, "bannerImage")} />
          {form.bannerImagePreview && <img src={form.bannerImagePreview} alt="banner" width="100%" className="mb-3" />}
        </div>
        <button className="btn btn-primary w-100" disabled={submitting}>
          {submitting ? "Saving..." : "Save Designer"}
        </button>
      </form >
    </div >
  );
}
